import { Stack } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CampaignSavedData,
  DropdownItem,
  TargetAudienceResponse,
} from "../../../../../types/apiTypes";
import { CustomButton } from "../../../../ui/CustomButton";
import { AiAssistantBlock } from "../../AIAssistantBlock";
import { useAnalyzeJobTitlesMutation } from "../../../../../api";
import { LabelWrapper } from "../AddPreferencesStep/LabelWrapper";
import { InputDropdown } from "../AddPreferencesStep/InputDropdown";
import { ItemType, SortableList } from "./SortableList";
import { toast } from "react-toastify";
import { CustomToast } from "../../../../ui/CustomToast";
import { trackEvent } from "../../../../../utils/analyticsService";
import { AiAssistantName, AnalyticsEvent } from "../../../../../types/events";

export const DefineTargetJobTitlesStep = ({
  initData,
  backHandler,
  nextStepHandler,
  setResultData,
  setPrevData,
}: {
  initData: CampaignSavedData;
  backHandler: () => void;
  nextStepHandler: () => void;
  setResultData: React.Dispatch<React.SetStateAction<CampaignSavedData>>;
  setPrevData: React.Dispatch<React.SetStateAction<CampaignSavedData>>;
}) => {
  const [titles, setTitles] = useState<TargetAudienceResponse<ItemType>[]>(
    initData.target_audience,
  );
  const [selectedSeniority, setSelectedSeniority] = useState<DropdownItem[]>(
    initData.selectedSeniority ||
      initData.target_audience.map(({ name, isAdded }) => ({
        id: name,
        value: name,
        isAdded,
      })),
  );

  const [analyzeJobTitles, { isLoading }] = useAnalyzeJobTitlesMutation();

  const seniorityList = useMemo(
    () =>
      titles.map(({ name }) => ({
        id: name,
        value: name,
        isAdded: false,
      })),
    [titles],
  );

  useEffect(() => {
    setResultData((prev) => ({
      ...prev,
      target_audience: titles,
      selectedSeniority,
    }));
  }, [titles, selectedSeniority]);

  const addAssistantData = async (text: string) => {
    trackEvent(AnalyticsEvent.AiAssistantUsed, {
      wizard_step: 3,
      assistant_name: AiAssistantName.JobTitles,
    });

    const parseTitles = await analyzeJobTitles({ text }).unwrap();

    const addedTitles: TargetAudienceResponse<ItemType>[] = [];

    titles.forEach((item) => {
      if (parseTitles.target_audience.some(({ name }) => name === item.name))
        return;
      addedTitles.push(item);
    });

    parseTitles.target_audience.forEach(
      ({ name, target_titles, ignored_titles }) => {
        const idx = titles.findIndex((title) => title.name === name);
        if (idx === -1) {
          addedTitles.push({
            name,
            isAdded: true,
            target_titles: target_titles.map((title) => ({
              name: title,
              isAdded: true,
              is_critical: true,
              id: `${name};${title}`,
            })),
            ignored_titles: ignored_titles.map((title) => ({
              name: title,
              isAdded: true,
              is_critical: true,
              id: `${name};${title}`,
            })),
          });

          return;
        }

        const origTitle = titles[idx];

        const addedTarget: ItemType[] = [...origTitle.target_titles];
        const addedIgnore: ItemType[] = [...origTitle.ignored_titles];

        target_titles.forEach((target) => {
          if (origTitle.target_titles.some(({ name }) => name === target))
            return;
          addedTarget.push({
            name: target,
            isAdded: true,
            is_critical: true,
            id: `${name};${target}`,
          });
        });

        ignored_titles.forEach((target) => {
          if (origTitle.target_titles.some(({ name }) => name === target))
            return;
          addedIgnore.push({
            name: target,
            isAdded: true,
            is_critical: true,
            id: `${name};${target}`,
          });
        });

        addedTitles.push({
          name,
          isAdded: true,
          target_titles: addedTarget,
          ignored_titles: addedIgnore,
        });
      },
    );

    setTitles(addedTitles);
    setSelectedSeniority(
      addedTitles.map(({ name, isAdded }) => ({
        id: name,
        value: name,
        isAdded,
      })),
    );
  };

  const changeCriticalHandler = (role: string, title: string) => {
    setTitles((prev) =>
      prev.map((item) => {
        if (item.name === role) {
          return {
            ...item,
            target_titles: item.target_titles.map((el) => ({
              ...el,
              is_critical: title === el.name ? !el.is_critical : el.is_critical,
            })),
            ignored_titles: item.ignored_titles.map((el) => ({
              ...el,
              is_critical: title === el.name ? !el.is_critical : el.is_critical,
            })),
          };
        }

        return item;
      }),
    );
  };
  const removeTitleHandler = (role: string, title: string) => {
    setTitles((prev) => {
      const state = prev.map((item) => {
        if (item.name === role) {
          return {
            ...item,
            target_titles: item.target_titles.filter((el) => el.name !== title),
            ignored_titles: item.ignored_titles.filter(
              (el) => el.name !== title,
            ),
          };
        }

        return item;
      });

      const updatedState = state.filter(
        (item) => item.ignored_titles.length || item.target_titles.length,
      );
      setSelectedSeniority((prev) =>
        prev.filter(({ value }) =>
          updatedState.some((item) => item.name === value),
        ),
      );
      return updatedState;
    });
  };

  const changeGroupHandler = (role: string, title: string, group: string) => {
    setTitles((prev) =>
      prev.map((item) => {
        if (item.name !== role) return item;

        const isIgnored = group === "ignored";
        const el = (isIgnored ? item.target_titles : item.ignored_titles).find(
          (elem) => elem.name === title,
        );

        if (!el) return item;

        return {
          ...item,
          target_titles: isIgnored
            ? item.target_titles.filter((el) => el.name !== title)
            : [...item.target_titles, el],
          ignored_titles: isIgnored
            ? [...item.ignored_titles, el]
            : item.ignored_titles.filter((el) => el.name !== title),
        };
      }),
    );
  };

  const { targetedList, ignoredList } = useMemo(() => {
    const targetedList: ItemType[] = [];
    const ignoredList: ItemType[] = [];

    titles.forEach(({ name, target_titles, ignored_titles }) => {
      if (selectedSeniority.some((selected) => selected.value === name)) {
        targetedList.push(...target_titles);
        ignoredList.push(...ignored_titles);
        return;
      }
    });

    return { targetedList, ignoredList };
  }, [titles, selectedSeniority]);

  const saveTitlesHandler = () => {
    setResultData((prev) => {
      const state = {
        ...prev,
        target_audiences: titles,
        target_titles: targetedList.map(({ is_critical, name }) => ({
          title: name,
          is_critical,
        })),
        ignored_titles: ignoredList.map(({ is_critical, name }) => ({
          title: name,
          is_critical,
        })),
        selectedSeniority,
      };

      setPrevData(state);

      return state;
    });
  };

  const goBackHandler = () => {
    saveTitlesHandler();
    backHandler();
  };

  const submitHandler = () => {
    saveTitlesHandler();
    nextStepHandler();
  };

  const addTitleHandler = useCallback(
    (title: string, group: string, onSuccess: () => void) => {
      const isInTarget = targetedList.some((item) => item.name === title);
      const isInIgnore = ignoredList.some((item) => item.name === title);

      if (title.length < 3) {
        toast.error(
          <CustomToast
            type="error"
            title="Invalid Job Title"
            text="The job title must contain at least 3 characters."
          />,
        );
        return;
      }

      if (isInTarget || isInIgnore) {
        toast.error(
          <CustomToast
            type="error"
            title="Invalid Job Title"
            text="Each job title must be unique. Please choose a different title."
          />,
        );
        return;
      }

      const unclassifiedRole = "Unclassified (manually added)";
      const isIgnoredRole = group === "ignored";
      const newEl = {
        id: `${unclassifiedRole};${title}`,
        name: title,
        isAdded: false,
        is_critical: true,
      };

      setTitles((prev) => {
        const el = prev.find((item) => item.name === unclassifiedRole);

        if (!el) {
          const updatedState = [
            ...prev,
            {
              name: unclassifiedRole,
              isAdded: false,
              ignored_titles: isIgnoredRole ? [newEl] : [],
              target_titles: isIgnoredRole ? [] : [newEl],
            },
          ];
          setSelectedSeniority((prev) => [
            ...prev,
            {
              id: unclassifiedRole,
              value: unclassifiedRole,
              isAdded: false,
            },
          ]);

          return updatedState;
        } else {
          return prev.map((item) => {
            if (item.name === unclassifiedRole) {
              const target_titles = item.target_titles;
              const ignored_titles = item.ignored_titles;

              isIgnoredRole
                ? ignored_titles.push(newEl)
                : target_titles.push(newEl);

              return {
                name: unclassifiedRole,
                isAdded: false,
                target_titles: isIgnoredRole
                  ? target_titles.filter((el) => el.name !== title)
                  : target_titles,
                ignored_titles: isIgnoredRole
                  ? ignored_titles
                  : ignored_titles.filter((el) => el.name !== title),
              };
            }

            return item;
          });
        }
      });
      onSuccess();
    },
    [targetedList, ignoredList],
  );

  return (
    <Stack gap="24px" width="100%">
      <AiAssistantBlock
        isLoading={isLoading}
        submitHandler={addAssistantData}
        title={"Describe your targeting, let AI build it"}
        subtitle={
          "AI extracts seniority, target titles, and titles to exclude - you review before anything is set."
        }
        placeholder={
          "E.g.\n" +
          "Seniority: Manager + \n" +
          "Functions: Information Security, Information Technology\n" +
          "Titles: \n" +
          "CIO/Chief Information Officer\n" +
          "CTO/Chief Technology Officer\n" +
          "SVP/VP/Head/Sr. Director/Director/Sr. Manager/Manager of IT/Information Technology\n" +
          "Ignore: Sales, Marketing, SVP/VP/Head/Sr. Director/Director/Sr. Manager/Manager of Global Risk" +
          " and Compliance (GRC)"
        }
      />
      <LabelWrapper
        gap={"24px"}
        title="Seniority Level"
        label="Choose the seniority levels you want to prioritize for this campaign."
      >
        <InputDropdown
          items={seniorityList}
          selected={selectedSeniority}
          setSelected={setSelectedSeniority}
          placeholder="Search Seniority Level(s)"
        />
      </LabelWrapper>
      <SortableList
        approvedList={targetedList}
        ignoredList={ignoredList}
        changeCriticalHandler={changeCriticalHandler}
        removeTitleHandler={removeTitleHandler}
        changeGroupHandler={changeGroupHandler}
        addTitleHandler={addTitleHandler}
      />

      <Stack direction="row" gap="8px">
        <CustomButton
          onClick={goBackHandler}
          variant="contained"
          textColor="#4C545B"
          fullWidth
          type="button"
          customColor="#F1F1F1"
        >
          Back
        </CustomButton>
        <CustomButton
          onClick={submitHandler}
          variant="contained"
          fullWidth
          type="button"
          customColor="#1C3C6C"
          disabled={selectedSeniority.length === 0}
        >
          Next Step
        </CustomButton>
      </Stack>
    </Stack>
  );
};
